import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalObject,
  getBoolean,
  getList,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { QuestionType } from "../types/questionType";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { Order, OrderDTO } from "../types/order";
import { GroupChildVisibilityWithQuestionDetails, GroupChildVisibilityWithQuestionDetailsDTO } from "./groupChildVisibilityWithQuestionDetails";

export interface SurveyQuestionViewDetailsDTO {
  surveyQuestionViewId: string;
  questionCode: CodeDTO;
  questionType: QuestionType.QuestionTypeDTO;
  question?: SurveyLocalizedTextDTO | null;
  isViewVisible: boolean;
  order: OrderDTO;
  groupChildVisibilities: GroupChildVisibilityWithQuestionDetailsDTO[];
}

export class SurveyQuestionViewDetails {
  readonly isSurveyQuestionViewDetailsSurveyQuestionViewDetails: boolean = true;

  readonly surveyQuestionViewId: LeoUUID;
  readonly questionCode: Code;
  readonly questionType: QuestionType.QuestionType;
  readonly question: SurveyLocalizedText | null;
  readonly isViewVisible: boolean;
  readonly order: Order;
  readonly groupChildVisibilities: GroupChildVisibilityWithQuestionDetails[];


  constructor(
  surveyQuestionViewId: LeoUUID, 
  questionCode: Code, 
  questionType: QuestionType.QuestionType, 
  question: SurveyLocalizedText | null = null, 
  isViewVisible: boolean, 
  order: Order, 
  groupChildVisibilities: GroupChildVisibilityWithQuestionDetails[]
  ) {
    this.surveyQuestionViewId = surveyQuestionViewId;
    this.questionCode = questionCode;
    this.questionType = questionType;
    this.question = question;
    this.isViewVisible = isViewVisible;
    this.order = order;
    this.groupChildVisibilities = groupChildVisibilities;
  }

  static fromDTO(dto: SurveyQuestionViewDetailsDTO): SurveyQuestionViewDetails {
    const surveyQuestionViewId = getLeoUuid(dto, "surveyQuestionViewId", InvalidSurveyQuestionViewDetailsSurveyQuestionViewIdError);
    const questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidSurveyQuestionViewDetailsQuestionCodeError));
    const questionType = QuestionType.fromDTO(getObject(dto, "questionType", InvalidSurveyQuestionViewDetailsQuestionTypeError));
    let question = getOptionalObject(dto, "question");
    if(question !== undefined && question !== null) {
      question = SurveyLocalizedText.fromDTO(getObject(dto, "question", InvalidSurveyQuestionViewDetailsQuestionError));
    }
    const isViewVisible = getBoolean(dto, "isViewVisible", InvalidSurveyQuestionViewDetailsIsViewVisibleError);
    const order = Order.fromDTO(getObject(dto, "order", InvalidSurveyQuestionViewDetailsOrderError));
    const groupChildVisibilities = getList<GroupChildVisibilityWithQuestionDetailsDTO, GroupChildVisibilityWithQuestionDetails>(
      dto,
      "groupChildVisibilities",
      GroupChildVisibilityWithQuestionDetails.fromDTO,
      InvalidSurveyQuestionViewDetailsGroupChildVisibilitiesError
    );
    return new SurveyQuestionViewDetails(
      surveyQuestionViewId,
      questionCode,
      questionType,
      question,
      isViewVisible,
      order,
      groupChildVisibilities
    );
  }

  toDTO(): SurveyQuestionViewDetailsDTO {
    const dto: SurveyQuestionViewDetailsDTO = {
        surveyQuestionViewId: this.surveyQuestionViewId.toDTO(),
        questionCode: this.questionCode.toDTO(),
        questionType: QuestionType.toDTO(this.questionType),
        isViewVisible: this.isViewVisible,
        order: this.order.toDTO(),
        groupChildVisibilities: this.groupChildVisibilities.map((e: GroupChildVisibilityWithQuestionDetails) => { return e.toDTO() })
    };
    if (this.question !== undefined && this.question !== null){
      dto.question = this.question.toDTO();
    }
    return dto;
  }
  }

SurveyQuestionViewDetails.prototype.toString = function toString(): string {
  return `SurveyQuestionViewDetails(surveyQuestionViewId='${this.surveyQuestionViewId}',questionCode='${this.questionCode}',questionType='${this.questionType}',question='${this.question}',isViewVisible='${this.isViewVisible}',order='${this.order}',groupChildVisibilities='${this.groupChildVisibilities}')`;
};

export class InvalidSurveyQuestionViewDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsError";
  }
}
export class InvalidSurveyQuestionViewDetailsSurveyQuestionViewIdError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsSurveyQuestionViewIdError";
  }
}

export class InvalidSurveyQuestionViewDetailsQuestionCodeError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsQuestionCodeError";
  }
}

export class InvalidSurveyQuestionViewDetailsQuestionTypeError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsQuestionTypeError";
  }
}

export class InvalidSurveyQuestionViewDetailsQuestionError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsQuestionError";
  }
}

export class InvalidSurveyQuestionViewDetailsIsViewVisibleError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsIsViewVisibleError";
  }
}

export class InvalidSurveyQuestionViewDetailsOrderError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsOrderError";
  }
}

export class InvalidSurveyQuestionViewDetailsGroupChildVisibilitiesError extends InvalidSurveyQuestionViewDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionViewDetailsGroupChildVisibilitiesError";
  }
}
